import React, { useState } from 'react';
import { Card, CardContent, Typography, Box, Collapse, IconButton } from '@mui/material';
import { ISubscriptionPlan } from '../../redux/subscription/types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import OutlinedCheckMark from '../CustomSVGIcons/OutlinedCheckMark';
import CircularCheckmark from '../CustomSVGIcons/CircularCheckIcon';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

interface PlanCardProps {
    plan: ISubscriptionPlan;
    isSelected?: boolean;
    onSelect?: () => void;
    planIndex?: number;
}

const formatCurrency = (price: number, interval: string, symbol: string = '$') => {
    return `${symbol}${price / 100}/${interval}`;
};

const PlanCard: React.FC<PlanCardProps> = ({ plan, isSelected = false, onSelect, planIndex }) => {
    const { themeStyle, darkModeEnabled, device } = useThemeContext();
    const [expanded, setExpanded] = useState(false);

    const isMobile = device.isMobile;

    const handleExpandClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (isMobile) {
            event.stopPropagation();
            setExpanded(!expanded);
        }
    };

    const selectColorForPlan = () => {
        if (darkModeEnabled) return theme[themeStyle].background_gradient.end;
        let color = '#F6F6FE ';
        switch (planIndex) {
            case 0:
                color = '#F6F6FE ';
                break;
            case 1:
                color = '#FFF9EB';
                break;
            case 2:
                color = '#fef6faff ';
                break;
        }
        return color;
    };

    return (
        <Card
            sx={{
                border:
                    isSelected || plan.preferred_plan ? `2px solid ${theme[themeStyle].highlight}` : '1px solid #ccc',
                borderRadius: '10px',
                padding: isMobile ? '12px' : '16px',
                boxShadow: isSelected ? `0px 4px 10px ${theme[themeStyle].selectableBorder}` : 'none',
                cursor: 'pointer',
                width: isMobile ? 'calc(100% - 24px)' : '250px',
                maxWidth: isMobile ? '100%' : '250px',
                height: isMobile ? 'auto' : '400px',
                backgroundColor: selectColorForPlan(),
                overflow: 'visible',
                m: isMobile ? '0' : 'auto',
                position: 'relative',
            }}
            onClick={onSelect}>
            {plan.preferred_plan && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: '#000',
                        color: '#fff',
                        px: 2,
                        py: 0.5,
                        borderRadius: '999px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        zIndex: 2,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                    }}>
                    Most Popular
                </Box>
            )}
            <CardContent sx={{ p: 0.5, width: '100%' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontSize: isMobile ? '1rem' : '2rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                        {plan.name}
                    </Typography>
                    <Box display="flex" alignItems="center" flexShrink={0}>
                        <CircularCheckmark filled={isSelected} color={theme[themeStyle].highlight} size={20} />
                        {isMobile && (
                            <IconButton
                                onClick={handleExpandClick}
                                aria-expanded={expanded}
                                aria-label="show more"
                                sx={{ ml: 1 }}>
                                {expanded ? (
                                    <KeyboardArrowUpIcon sx={{ color: theme[themeStyle].highlight }} />
                                ) : (
                                    <KeyboardArrowDownIcon sx={{ color: theme[themeStyle].highlight }} />
                                )}
                            </IconButton>
                        )}
                    </Box>
                </Box>
                {plan.converted ? (
                    <>
                        <Typography
                            variant="h4"
                            fontWeight="bold"
                            color="primary"
                            mt={1}
                            sx={{
                                color: theme[themeStyle].highlight,
                                fontSize: {
                                    xs: '1rem',
                                    sm: '1.2rem',
                                },
                            }}>
                            {plan.converted_amount} / {plan.interval}
                        </Typography>
                        <Typography
                            variant="h4"
                            fontWeight="bold"
                            color="primary"
                            mt={1}
                            sx={{
                                color: theme[themeStyle].highlight,
                                fontSize: {
                                    xs: '0.8rem',
                                    sm: '0.8rem',
                                },
                            }}>
                            Aprox. {formatCurrency(plan.amount, plan.interval)}
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography
                            variant="h4"
                            fontWeight="bold"
                            color="primary"
                            mt={1}
                            sx={{
                                color: theme[themeStyle].highlight,
                                fontSize: {
                                    xs: '1rem',
                                    sm: '1.2rem',
                                },
                            }}>
                            {formatCurrency(plan.amount, plan.interval, plan.toSymbol)}
                        </Typography>
                    </>
                )}
                <Typography
                    style={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: theme[themeStyle].tag_white,
                        fontSize: 12,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                    mt={1}>
                    {plan.description}
                </Typography>

                {isMobile ? (
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box mt={2}>
                            {plan.features.map((feature, index) => (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    key={index}
                                    mt={1}
                                    sx={{
                                        '& > svg': {
                                            marginRight: '8px',
                                        },
                                    }}>
                                    <OutlinedCheckMark
                                        stroke={theme[themeStyle].highlight}
                                        width="20px"
                                        height="20px"
                                    />
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{ textAlign: 'left', color: theme[themeStyle].tag_white }}>
                                        {feature}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Collapse>
                ) : (
                    <Box mt={2}>
                        {plan.features.map((feature, index) => (
                            <Box
                                display="flex"
                                alignItems="center"
                                key={index}
                                mt={1}
                                sx={{
                                    '& > svg': {
                                        marginRight: '8px',
                                    },
                                }}>
                                <OutlinedCheckMark stroke={theme[themeStyle].highlight} width="20px" height="20px" />
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ textAlign: 'left', color: theme[themeStyle].tag_white }}>
                                    {feature}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default PlanCard;
